import * as actionTypes from '../../constants/actionTypes';
import { CALL_PRIVATE_API } from '../../middleware/api';
import { sourceSchema } from '../../schemas';
import { getCurrentOrg, getCurrentUser } from '../../reducers/selectors';
import { DELETE, GET, POST, PUT } from '../../constants/requestMethods';
import { RESOURCE_TYPE_SOURCE } from '../../constants/resourceTypes';
import {
  ACLQueryModeTypes,
  RESOURCE_SUGGESTIONS_COUNT,
  SearchFilterViews,
} from '../../constants/visibilityFilters';
import {
  CONNECTION_CATEGORY_FILE_UPLOAD,
  CONNECTION_CATEGORY_SYSLOG,
  CONNECTION_CATEGORY_TEMPLATIZED_EMAIL_SCRIPT,
  CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT,
  CONNECTION_CATEGORY_WEBHOOK,
  CONNECTOR_CODE_SQL_RUNNER,
} from '../../constants/connectionCategories';
import { FILES_PAGINATED } from '../../constants/paginationEntitiesTypes';
import {
  ACTIVATE_SOURCE,
  COPY_SOURCE,
  RUN_SOURCE_NOW,
  DELETE_SOURCE,
  PAUSE_SOURCE,
  UPLOAD_FILE,
} from '../../constants/processTypes';
import Constants from '../../Constants';
import UIUtils from '../../lib/UIUtils';
import { isUiModeCode } from 'new/utils/uiMode';
import { getResourceApiKeys, setReferencedResourceIds } from '../../lib/ResourceUtils';
import { getUsableApiKey, getUsableApiKeyEntity, getWebhookUrl } from '../../lib/URLUtils';
import { processSearchFilters } from '../../lib/SearchFilterUtils';
import { fetchResourceFiles, fetchResourceFileStats } from '../metrics';
import { addProcess, removeProcess } from '../processes';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import has from 'lodash/has';
import includes from 'lodash/includes';
import isBoolean from 'lodash/isBoolean';
import { loadDataSink, refreshPipeline } from 'js/actions';
import { tryCatchAsync } from 'new/utils/TryCatch';
import { addTriggers, deleteTriggers, loadTriggers } from 'new/state/flowTriggers';
import { FlowTriggerHelper } from 'js/components/data_sources/add_ds_form/MonitoringInterval/FlowTriggerHelper';
import {
  getOrgConfigOverrides,
  applyOrgConfigOverrides,
} from 'new/utils/FeatureUtil/orgConfigOverrides';
import flowTypes from 'new/components/flows/types/flowTypes';
import { debugResourcePayload, isResourceDebugEnabled } from 'js/lib/ResourcePayloadDebugger';
import { getResourceNameWithFallback } from '../../lib/ResourceNameUtils';
import { normalizeCredentialId } from '../../lib/utils/credentialUtils';

export const loadDataSources =
  (page, sortCriteria, availableFilters, options = {}) =>
  (dispatch, getState) => {
    const {
      app: {
        accessControl,
        visibilityFilters: { filters },
      },
    } = getState();

    const aclQueryMode = options?.aclQueryMode ? options.aclQueryMode : accessControl.mode;

    const { sortKey, sortOrder } = Constants.SortCriteriaInfo[sortCriteria];
    const { processedFilters } = processSearchFilters(
      filters[SearchFilterViews.SOURCES],
      availableFilters,
    );
    let requestMethod;
    let endpoint;
    let payload;

    if (isEmpty(processedFilters)) {
      requestMethod = GET;
      endpoint = '/data_sources';
      payload = {
        page,
        per_page: Constants.LISTING_ITEMS_PER_PAGE,
        sort_by: sortKey,
        sort_order: sortOrder,
        // Sources belonging to agentic (MCP Studio) flows are hidden from the list views
        agentic: false,
      };
    } else {
      requestMethod = POST;
      endpoint = `/data_sources/search?page=${page}&per_page=${Constants.LISTING_ITEMS_PER_PAGE}&sort_by=${sortKey}&sort_order=${sortOrder}&agentic=false`;
      payload = { filters: processedFilters };
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_DATA_SOURCES_REQUEST,
          actionTypes.GET_DATA_SOURCES_SUCCESS,
          actionTypes.GET_DATA_SOURCES_FAILURE,
        ],
        endpoint,
        schema: [sourceSchema],
        requestMethod,
        aclQueryMode,
        payload,
        requestInfo: { view: SearchFilterViews.SOURCES },
      },
    }).then((response) => {
      return response;
    });
  };

export const loadDataSource =
  (
    id,
    { aclQueryMode, ignoreErrors } = {
      aclQueryMode: ACLQueryModeTypes.MODE_OWNER.key,
    },
  ) =>
  async (dispatch) => {
    const request = dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_DATA_SOURCE_REQUEST,
          actionTypes.GET_DATA_SOURCE_SUCCESS,
          actionTypes.GET_DATA_SOURCE_FAILURE,
        ],
        endpoint: `/data_sources/${id}`,
        schema: sourceSchema,
        payload: {
          expand: 1,
        },
        requestMethod: GET,
        ignoreErrors,
        aclQueryMode,
      },
    });

    const { data: response, error } = await tryCatchAsync(request);

    if (error) {
      return error;
    }

    const { entities, result } = response;

    const source = entities.sources[result];
    if (source?.flow_triggers) {
      const resourcesRequests = [];
      source.flow_triggers.map((trigger) => {
        if (trigger.triggering_resource_type === 'data_sink') {
          resourcesRequests.push(dispatch(loadDataSink(trigger.triggering_resource_id)));
        }
      });
      await Promise.all(resourcesRequests);
    }
    return response;
  };

export function loadDataSourceSearchSuggestions(filters, availableFilters) {
  return (dispatch, getState) => {
    const { app } = getState();
    const aclQueryMode = app.accessControl.mode;

    const sortCriteria = Constants.SortCriteria.LAST_CREATED_DESC;
    const { sortKey, sortOrder } = Constants.SortCriteriaInfo[sortCriteria];

    const { processedFilters } = processSearchFilters(filters, availableFilters);

    if (isEmpty(processedFilters)) {
      return;
    }

    const params = new URLSearchParams({
      page: 1,
      per_page: RESOURCE_SUGGESTIONS_COUNT,
      sort_by: sortKey,
      sort_order: sortOrder,
      agentic: false,
    });

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.SEARCH_DATA_SOURCES_REQUEST,
          actionTypes.SEARCH_DATA_SOURCES_SUCCESS,
          actionTypes.SEARCH_DATA_SOURCES_FAILURE,
        ],
        endpoint: `/data_sources/search?${params}`,
        schema: [sourceSchema],
        requestMethod: POST,
        aclQueryMode,
        payload: { filters: processedFilters },
        requestInfo: { view: SearchFilterViews.SOURCES },
      },
    });
  };
}

export const addDataSource =
  (
    sourceType,
    connectionCategory = null,
    info = null,
    credentialId = null,
    ignoreCredentials = false,
  ) =>
  (dispatch, getState) => {
    if (
      !info ||
      (!includes(
        [
          CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT,
          CONNECTION_CATEGORY_TEMPLATIZED_EMAIL_SCRIPT,
          CONNECTION_CATEGORY_WEBHOOK,
          CONNECTION_CATEGORY_FILE_UPLOAD,
          CONNECTION_CATEGORY_SYSLOG,
        ],
        connectionCategory,
      ) &&
        !credentialId)
    ) {
      return dispatch({
        type: actionTypes.ADD_DATA_SOURCE_FAILURE,
        payload: {
          error: 'Insufficient source information',
        },
      });
    }

    let payload = {
      ...info,
      /**
       * avoid sending an empty object, because in case it is rest templatized api,
       * the API could use the empty source_config instead of templatized_config
       */
      source_config: info?.source_config ? { ...info.source_config } : undefined,
      ingest_method: sourceType === 'nexla_rest' ? 'API' : 'POLL',
    };

    //TODO: temp hack for email script

    if (
      connectionCategory !== CONNECTION_CATEGORY_TEMPLATIZED_EMAIL_SCRIPT &&
      connectionCategory !== CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT
    ) {
      payload.source_type = sourceType;
    }

    // NEX-21470: in code mode the user owns the source_config entirely.
    // Skip the legacy field-strip / flow_triggers extract / adaptive_flow
    // extract / typecast / org-override mutations. Form-mode behavior
    // unchanged.
    const isCodeMode = isUiModeCode(info?.source_config);

    //TODO: temp hack to db query statement.
    if (!isCodeMode && has(info, 'source_config') && has(info.source_config, 'db_query_mode')) {
      if (info.source_config.db_query_mode === 'Query') {
        delete payload.source_config['table'];
      } else if (has(info.source_config, 'query')) {
        delete payload.source_config['query'];
      }
    }

    if (!isCodeMode) {
      const flowTriggers =
        info?.source_config?.flow_triggers ||
        info?.source_config?.uiconfig?.flow_triggers ||
        info?.script_config?.config?.flow_triggers ||
        info?.template_config?.flow_triggers;

      if (!!flowTriggers) {
        payload.flow_triggers = flowTriggers;
      }

      delete payload.source_config?.flow_triggers;
      delete payload.script_config?.config?.flow_triggers;
      delete payload.template_config?.flow_triggers;

      const adaptiveFlow =
        'adaptive_flow' in (info?.source_config || {})
          ? info.source_config.adaptive_flow
          : 'adaptive_flow' in (info?.template_config || {})
            ? info.template_config.adaptive_flow
            : undefined;

      if (isBoolean(adaptiveFlow)) {
        /**
         * handle adaptive_flow case
         * 1. add adaptive_flow to the source payload
         * 2. remove adaptive_flow from the template_config and source_config
         */
        payload['adaptive_flow'] = adaptiveFlow;
        delete payload.template_config?.adaptive_flow;
        delete payload.source_config?.adaptive_flow;
      }
    }

    // TODO: temp hack to remove name/description from source_config which might
    // have been added by JSON mode
    delete payload.source_config?.name;
    delete payload.source_config?.description;

    if (!isCodeMode) {
      _typeCastSourceConfigValues(payload, getState());
    }

    const { entities: { credentials = {} } = {}, paging: { metaInfoById = {} } = {} } = getState();
    const credentialIdToSend = normalizeCredentialId(credentialId);
    const credential =
      credentialId != null && typeof credentialId === 'object'
        ? credentials[credentialIdToSend] || credentialId
        : credentials[credentialIdToSend];

    if (ignoreCredentials !== true && !isNil(credentialIdToSend)) {
      payload.data_credentials_id = credentialIdToSend;
    }

    // check if parent_data_set_id is provided (for API server flows), include it in the payload
    if (info?.parent_data_set_id) {
      payload.parent_data_set_id = info.parent_data_set_id;
    }

    payload = setReferencedResourceIds(payload);

    //check if the source_config should have any overrides. if so apply them. Be careful not to screw up orgs that should not have overrides.
    const org = getCurrentOrg(getState());
    // flow_type is either present in info or undefined.
    const flowType = info?.flow_type || flowTypes.streaming;

    // NEX-21470: do not inject org overrides in code mode.
    const orgConfigOverrides = !isCodeMode
      ? getOrgConfigOverrides(org.id, 'source_config', connectionCategory, flowType)
      : null;
    if (orgConfigOverrides) {
      payload.source_config = applyOrgConfigOverrides(payload.source_config, orgConfigOverrides);
      //TODO: this is temporary logging. Remove this when orgOverrides are well tested.
      console.info('Source Creation: orgConfigOverrides', orgConfigOverrides);
      console.info('Source Creation: source_config', payload.source_config);
    }

    payload.name = getResourceNameWithFallback({
      currentName: payload.name,
      connectionType: sourceType,
      metaInfo: metaInfoById[sourceType] || metaInfoById[payload.source_type],
      credential,
      resourceLabel: 'Source',
    });

    // Debug mode: log payload and optionally block API call
    if (isResourceDebugEnabled()) {
      const shouldBlock = debugResourcePayload('CREATE_SOURCE', payload, {
        endpoint: '/data_sources',
        resourceType: sourceType,
        connectionCategory,
        flowType,
      });

      if (shouldBlock) {
        // Dispatch failure action to trigger normal error handling in components
        // This resets component states properly without modifying the component
        dispatch({
          type: actionTypes.ADD_DATA_SOURCE_FAILURE,
          error: 'Blocked by debugger',
        });
        return Promise.resolve({ debuggerBlocked: true });
      }
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.ADD_DATA_SOURCE_REQUEST,
          actionTypes.ADD_DATA_SOURCE_SUCCESS,
          actionTypes.ADD_DATA_SOURCE_FAILURE,
        ],
        endpoint: `/data_sources`,
        schema: sourceSchema,
        requestMethod: POST,
        payload,
      },
    });
  };

export const activateSource = (id) => (dispatch, getState) => {
  const {
    entities: { sources },
  } = getState();
  const source = sources[id];

  dispatch(addProcess(ACTIVATE_SOURCE, `Activating Source "${source.name}" (ID: ${id})`, { id }));

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.ACTIVATE_SOURCE_REQUEST,
        actionTypes.UPDATE_SOURCE_INFO_SUCCESS,
        actionTypes.UPDATE_SOURCE_INFO_FAILURE,
      ],
      endpoint: `/data_sources/${id}/activate`,
      schema: sourceSchema,
      requestMethod: PUT,
    },
  })
    .then(() => {
      if (source?.origin_node_id) {
        // Adding this line to refresh pipeline so nexsets can have their status updated
        dispatch(refreshPipeline(source.origin_node_id));
      }
    })
    .finally(() => {
      dispatch(removeProcess(ACTIVATE_SOURCE, { id }));
    });
};

export const pauseSource = (id) => (dispatch, getState) => {
  const {
    entities: { sources },
  } = getState();
  const source = sources[id];

  dispatch(
    addProcess(PAUSE_SOURCE, `Pausing Source "${source.name}" (ID: ${id})`, {
      id,
    }),
  );

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.PAUSE_SOURCE_REQUEST,
        actionTypes.UPDATE_SOURCE_INFO_SUCCESS,
        actionTypes.UPDATE_SOURCE_INFO_FAILURE,
      ],
      endpoint: `/data_sources/${id}/pause`,
      schema: sourceSchema,
      requestMethod: PUT,
    },
  })
    .then(() => {
      if (source?.origin_node_id) {
        // Adding this line to refresh pipeline so nexsets can have their status updated
        dispatch(refreshPipeline(source.origin_node_id));
      }
    })
    .finally(() => {
      dispatch(removeProcess(PAUSE_SOURCE, { id }));
    });
};

export const overrideRuntimeStatus = (id, runtimeStatus) => (dispatch, getState) => {
  const {
    entities: { sources, flowNodes },
  } = getState();
  const source = sources[id];

  if (!source) {
    return;
  }

  const flowNodeId = source.flow_node_id;
  const flowNode = flowNodes[flowNodeId];

  // update the source runtime_status locally
  const entities = {
    sources: {
      [id]: {
        ...source,
        runtime_status: runtimeStatus,
      },
    },
  };

  // also update the flow node runtime_status if it exists
  if (flowNode) {
    entities.flowNodes = {
      [flowNodeId]: {
        ...flowNode,
        runtime_status: runtimeStatus,
      },
    };
  }

  dispatch({
    type: actionTypes.OVERRIDE_RUNTIME_STATUS,
    entities,
  });
};

export const runSource = (id) => (dispatch, getState) => {
  const state = getState();
  const {
    entities: { sources },
  } = state;
  const source = sources[id];

  dispatch(
    addProcess(RUN_SOURCE_NOW, `Starting Source "${source.name}" (ID: ${id})`, {
      id,
    }),
  );

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.RUN_SOURCE_NOW_REQUEST,
        actionTypes.RUN_SOURCE_NOW_SUCCESS,
        actionTypes.RUN_SOURCE_NOW_FAILURE,
      ],
      endpoint: `/data_sources/${id}/run_now`,
      schema: null,
      requestMethod: PUT,
    },
  })
    .then(() => {
      if (source?.origin_node_id) {
        // Refresh pipeline to get updated state
        return dispatch(refreshPipeline(source.origin_node_id));
      }
    })
    .then(() => {
      dispatch(overrideRuntimeStatus(id, 'PROCESSING'));
    })
    .finally(() => {
      dispatch(removeProcess(RUN_SOURCE_NOW, { id }));
    });
};

export const updateSourceInfo =
  (
    id,
    newInfo = {},
    connectionCategory = null,
    selectedCredentialId = null,
    runWithIngestion = false,
  ) =>
  async (dispatch, getState) => {
    let payload = { ...newInfo };

    const {
      entities: { sources, vendors, credentials = {} },
      paging: { metaInfoById = {} } = {},
    } = getState();
    const currentSource = sources[id];

    // TODO: Temp: sql_runner vendor does not accept source_type changes on update
    let isSqlRunner = false;
    if (connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT) {
      const vendor = vendors?.[currentSource?.vendor_id];
      if (vendor?.name === CONNECTOR_CODE_SQL_RUNNER) {
        isSqlRunner = true;
        delete payload.source_type;
      }
    }

    // NEX-21470: code-mode updates send source_config verbatim.
    const isCodeModeUpdate = isUiModeCode(newInfo?.source_config);

    //TODO: temp hack to db query statement.
    if (
      !isCodeModeUpdate &&
      has(newInfo, 'source_config') &&
      has(newInfo.source_config, 'db_query_mode')
    ) {
      if (newInfo.source_config.db_query_mode === 'Query') {
        delete payload.source_config['table'];
      } else if (has(newInfo.source_config, 'query')) {
        delete payload.source_config['query'];
      }
    }

    // update source_config of templatized script sources
    if (
      connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT ||
      connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_EMAIL_SCRIPT
    ) {
      const {
        entities: { sources },
      } = getState();
      const source = sources[id];

      if (payload.source_config) {
        payload.source_config = {
          ...source.source_config,
          ...payload.source_config,
        };
      }
    }

    // For sql_runner, don't override data_credentials_id - it's already correctly set from UIUtils
    // IMPORTANT: sql_runner has multiple credential fields (one per storage_type), so we must use
    // the credential that was extracted by buildSqlRunnerSourceFromTemplatizedScriptConfig() based on
    // storage_type matching. We do NOT use selectedCredentialId here because it doesn't properly track
    // which credential field corresponds to the (locked) storage_type. The UIUtils function handles
    // extracting the correct credential from the visible/editable credential field and preserving it
    // unless the user explicitly selects a different one. Note: storage_type is disabled in edit mode.
    const selectedCredentialIdToSend = normalizeCredentialId(selectedCredentialId);
    if (!!selectedCredentialIdToSend && !isSqlRunner) {
      payload.data_credentials_id = selectedCredentialIdToSend;
    }

    if (!isCodeModeUpdate) {
      const flowTriggers =
        newInfo?.source_config?.flow_triggers ||
        newInfo?.source_config?.uiconfig?.flow_triggers ||
        newInfo?.script_config?.config?.flow_triggers ||
        newInfo?.script_config?.flow_triggers ||
        newInfo?.template_config?.flow_triggers;

      const adaptiveFlow =
        'adaptive_flow' in (newInfo?.source_config || {})
          ? newInfo.source_config.adaptive_flow
          : 'adaptive_flow' in (newInfo?.template_config || {})
            ? newInfo.template_config.adaptive_flow
            : undefined;

      // NEX-22548: sync only what the user changed. No trigger list in the config means
      // "triggers not edited" (e.g. the flow card's Schedule dialog) — leave them alone,
      // even if a cron is present. An external trigger (adaptive_flow) replaces them all.
      // The list may be the source's raw API `flow_triggers`, which also holds triggers
      // where this source starts ANOTHER flow; planTriggerSync ignores those. Unchanged
      // triggers keep their id and PAUSED status, and adding before deleting means a
      // failed add never leaves the source without its triggers.
      const desiredTriggers = adaptiveFlow === true ? [] : flowTriggers;
      // The edit forms copy the source's API triggers (each with an `id`) into the config;
      // the picker only ever produces new entries (no `id`) or [] — so an all-`id` list
      // is the untouched copy and needs no sync (not even a read).
      const triggersEdited =
        Array.isArray(desiredTriggers) &&
        !(desiredTriggers.length > 0 && desiredTriggers.every((t) => t?.id != null));
      let savedTriggers = currentSource?.flow_triggers ?? [];
      let toAdd = [];
      let toDelete = [];
      if (triggersEdited) {
        // Plan against the API, not the store: a source loaded from the flow canvas
        // carries no flow_triggers, so its existing triggers would never be removed.
        const { result } = await dispatch(loadTriggers(id));
        savedTriggers = Array.isArray(result) ? result : [];
        ({ toAdd, toDelete } = FlowTriggerHelper.planTriggerSync(
          { ...currentSource, flow_triggers: savedTriggers },
          desiredTriggers,
        ));
      }
      const blockTriggerCalls =
        isResourceDebugEnabled() &&
        debugResourcePayload(
          'UPDATE_SOURCE_TRIGGERS',
          {
            savedTriggers,
            requested: triggersEdited ? desiredTriggers : 'none (triggers untouched)',
            toAdd,
            toDelete,
          },
          { endpoint: `/data_sources/${id}/flow_triggers`, resourceId: id },
        );
      if (!blockTriggerCalls) {
        if (toAdd.length > 0) {
          await dispatch(addTriggers(id, toAdd));
        }
        if (toDelete.length > 0) {
          await dispatch(deleteTriggers(id, toDelete));
        }
      }
      delete payload?.source_config?.flow_triggers;
      delete payload?.source_config?.uiconfig?.flow_triggers;
      delete payload?.script_config?.config?.flow_triggers;
      delete payload?.script_config?.flow_triggers;
      delete payload?.template_config?.flow_triggers;

      if (isBoolean(adaptiveFlow)) {
        payload['adaptive_flow'] = adaptiveFlow;
        delete payload?.template_config?.['adaptive_flow'];
        delete payload?.source_config?.['adaptive_flow'];
      }
    }

    // TODO: temp hack to remove name/description from source_config which might
    // have been added by JSON mode
    delete payload.source_config?.name;
    delete payload.source_config?.description;

    if (!isCodeModeUpdate) {
      _typeCastSourceConfigValues(payload, getState());
    }

    payload = setReferencedResourceIds(payload);

    if (has(payload, 'name')) {
      const credentialCandidate =
        selectedCredentialId ??
        payload.data_credentials_id ??
        currentSource?.data_credentials_id ??
        currentSource?.data_credentials;
      const credentialId = normalizeCredentialId(credentialCandidate);
      const credential =
        credentials[credentialId] ||
        (credentialCandidate != null && typeof credentialCandidate === 'object'
          ? credentialCandidate
          : undefined);
      const connectionType = payload.source_type || currentSource?.source_type;
      const currentName =
        payload.name != null && String(payload.name).trim() ? payload.name : currentSource?.name;

      payload.name = getResourceNameWithFallback({
        currentName,
        connectionType,
        metaInfo: metaInfoById[connectionType],
        credential,
        resourceLabel: 'Source',
      });
    }

    // Debug mode: log payload and optionally block API call
    if (isResourceDebugEnabled()) {
      const shouldBlock = debugResourcePayload('UPDATE_SOURCE', payload, {
        endpoint: `/data_sources/${id}`,
        resourceId: id,
        connectionCategory,
        runWithIngestion,
      });

      if (shouldBlock) {
        // Dispatch failure action to trigger normal error handling in components
        dispatch({
          type: actionTypes.UPDATE_SOURCE_INFO_FAILURE,
          error: 'Blocked by debugger',
        });
        return Promise.resolve({ debuggerBlocked: true });
      }
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.UPDATE_SOURCE_INFO_REQUEST,
          actionTypes.UPDATE_SOURCE_INFO_SUCCESS,
          actionTypes.UPDATE_SOURCE_INFO_FAILURE,
        ],
        endpoint: `/data_sources/${id}?run_now=${runWithIngestion}`,
        schema: sourceSchema,
        requestMethod: PUT,
        payload,
      },
    });
  };

export const deleteSource = (id) => (dispatch, getState) => {
  const {
    entities: { sources },
  } = getState();
  const source = sources[id];

  dispatch(addProcess(DELETE_SOURCE, `Deleting Source "${source.name}"`, { id }));

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.DELETE_SOURCE_REQUEST,
        actionTypes.DELETE_SOURCE_SUCCESS,
        actionTypes.DELETE_SOURCE_FAILURE,
      ],
      endpoint: `/data_sources/${id}`,
      schema: sourceSchema,
      requestMethod: DELETE,
      requestInfo: {
        id,
        nodeKey: source.flow_node_id,
      },
    },
  })
    .then(() => {
      // if the source is not the origin node, refresh the pipeline (ex. Target node in API Server Flow)
      if (source?.origin_node_id && source.origin_node_id !== source.flow_node_id) {
        dispatch(refreshPipeline(source.origin_node_id));
      }
    })
    .finally(() => {
      dispatch(removeProcess(DELETE_SOURCE, { id }));
    });
};

/**
 * @param id
 * @param payload
 * @param filters
 *    @param autoRefresh
 *    @property filters.page number
 *    @property filters.size: number
 *    @property filters.orderby string
 *    @property filters.status string
 * @returns {function(*): *}
 */
export const reingestFile =
  (id, payload, filters, autoRefresh = true) =>
  (dispatch) => {
    if (isNil(payload) || !Array.isArray(payload) || !payload.length) {
      console.log('Need an array with at least one file to reingest');
      return;
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.FILE_REINGESTION_REQUEST,
          actionTypes.FILE_REINGESTION_SUCCESS,
          actionTypes.FILE_REINGESTION_FAILURE,
        ],
        endpoint: `/data_sources/${id}/file/ingest`,
        schema: null,
        requestMethod: POST,
        payload,
        requestInfo: {
          id,
          resourceType: 'data_sources',
          paginatedResource: FILES_PAGINATED,
        },
      },
    })
      .then(() => {
        if (autoRefresh) {
          return dispatch(fetchResourceFiles(id, RESOURCE_TYPE_SOURCE, filters, filters.orderby));
        }

        return Promise.resolve();
      })
      .then(() => {
        if (autoRefresh) return dispatch(fetchResourceFileStats(id, RESOURCE_TYPE_SOURCE));

        return Promise.resolve();
      });
  };

export const uploadFileToSource = (id, file) => async (dispatch, getState) => {
  dispatch(
    addProcess(
      UPLOAD_FILE,
      `Uploading file "${file.name}"`,
      { id },
      false,
      actionTypes.UPLOAD_FILE_TO_SOURCE_REQUEST,
    ),
  );

  let state = getState();
  const {
    entities: { sources, apiKeys },
  } = state;

  const source = sources[id];
  if (!source) {
    dispatch(removeProcess(UPLOAD_FILE, { id }));
    return Promise.reject(new Error('Source not found'));
  }

  const resourceApiKeys = getResourceApiKeys(source, apiKeys);
  let usableApiKeyString = getUsableApiKey(resourceApiKeys);

  if (!usableApiKeyString) {
    await dispatch(loadDataSource(id));
    state = getState();
  }

  const currentSource = state.entities.sources[id];
  const currentResourceApiKeys = getResourceApiKeys(currentSource, state.entities.apiKeys);
  const apiKeyEntity = getUsableApiKeyEntity(currentResourceApiKeys);
  const endpoint =
    apiKeyEntity && 'url' in apiKeyEntity && apiKeyEntity.url
      ? apiKeyEntity.url
      : getWebhookUrl(id, getCurrentOrg(state), currentResourceApiKeys, null, '/file');

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.UPLOAD_FILE_TO_SOURCE_REQUEST,
        actionTypes.UPLOAD_FILE_TO_SOURCE_SUCCESS,
        actionTypes.UPLOAD_FILE_TO_SOURCE_FAILURE,
      ],
      endpoint,
      schema: null,
      requestMethod: POST,
      isMultipart: true,
      payload: {
        data: file,
      },
      requestInfo: {
        id,
      },
    },
  }).finally(() => {
    dispatch(removeProcess(UPLOAD_FILE, { id }));
  });
};

export const copySource =
  (fromSourceId = null) =>
  (dispatch, getState) => {
    if (!fromSourceId) {
      return dispatch({
        type: actionTypes.COPY_DATA_SOURCE_FAILURE,
        error: 'Insufficient source information',
      });
    }

    const state = getState();
    const {
      entities: { sources },
    } = state;

    const fromSource = sources[fromSourceId];
    dispatch(
      addProcess(COPY_SOURCE, `Duplicating Source "${fromSource.name}"`, {
        id: fromSourceId,
      }),
    );

    let payload = {
      copy_access_controls: false,
      reuse_data_credentials: true,
      copy_other_data_sinks: false,
    };

    const user = getCurrentUser(state);
    const org = getCurrentOrg(state);
    if (!isEmpty(user) && !isEmpty(org)) {
      payload.owner_id = user.id;
      payload.org_id = org.id;
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.COPY_DATA_SOURCE_REQUEST,
          actionTypes.COPY_DATA_SOURCE_SUCCESS,
          actionTypes.COPY_DATA_SOURCE_FAILURE,
        ],
        endpoint: `/data_sources/${fromSourceId}/copy`,
        schema: sourceSchema,
        requestMethod: POST,
        payload,
      },
    }).finally(() => {
      dispatch(removeProcess(COPY_SOURCE, { id: fromSourceId }));
    });
  };

const _typeCastSourceConfigValues = (payload, state) => {
  if (payload.source_config && payload.source_type) {
    let {
      paging: { sourceConfigById },
    } = state;
    let sourceConfig = UIUtils.castToFieldType(
      payload.source_config,
      sourceConfigById[payload.source_type],
    );
    payload.source_config = sourceConfig;
  }
};
